import { useEffect, useRef, useState } from 'react'

import {
  Box,
  Button,
  CloseButton,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Text,
} from '@chakra-ui/react'
import Cropper from 'react-avatar-editor'

type EditPhotoModalProps = {
  isOpen: boolean
  onSave: (canvas: HTMLCanvasElement) => void
  onClose: () => void
  imageUrl: string
  cropShape?: 'rect' | 'round'
  aspectRatio?: number
  secondCutWidth?: number
  title?: string
  subtitle?: string
  loading: boolean
}

const EditPhotoModal = ({
  isOpen,
  onSave,
  onClose,
  imageUrl,
  loading,
  aspectRatio = 1,
  cropShape = 'rect',
  title = 'Edit Photo',
  subtitle,
  secondCutWidth,
}: EditPhotoModalProps) => {
  const editor = useRef(null)
  const [zoom, setZoom] = useState(1)
  const w = aspectRatio === 1 ? 238 : 292
  const h = w / aspectRatio

  useEffect(() => {
    if (isOpen) {
      setZoom(1)
    }
  }, [isOpen])

  const onClickSave = () => onSave((editor.current as Cropper).getImage())

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      motionPreset="slideInBottom"
      isCentered
      variant="cropper"
      size="sm"
    >
      <ModalOverlay />
      <ModalContent width="326px">
        <ModalHeader color="onyx">
          <CloseButton
            variant="boxModal"
            m="11px 4px"
            onClick={onClose}
            position="absolute"
            top={0}
            left="10px"
          />
          <h5 className="thick">{title}</h5>
        </ModalHeader>
        <ModalBody my={4}>
          {!!subtitle && (
            <Box className="body-small" color="stone" textAlign="center" mb={4}>
              {subtitle}
            </Box>
          )}
          <Flex direction="column" alignItems="center">
            <Box
              height={h}
              bg="onyx"
              w={w}
              position="relative"
              overflow="hidden"
              borderRadius="8px"
              data-testid="photoUpload-cropper"
              border="1px solid"
              borderColor="sandstone"
            >
              <Cropper
                ref={editor}
                image={imageUrl}
                width={w}
                height={h}
                border={0}
                scale={zoom}
                backgroundColor="#fff"
              />
              {cropShape === 'round' && (
                <Box
                  position="absolute"
                  top={-12}
                  bottom={-12}
                  left={-12}
                  right={-12}
                  borderRadius="full"
                  border="52px solid rgba(255, 255, 255, 0.5)"
                  pointerEvents="none"
                />
              )}
              {!!secondCutWidth && (
                <Box
                  position="absolute"
                  top={0}
                  bottom={0}
                  left={`${(w - secondCutWidth) / 2}px`}
                  right={`${(w - secondCutWidth) / 2}px`}
                  borderLeft="2px dotted rgba(255, 255, 255, 0.5)"
                  borderRight="2px dotted rgba(255, 255, 255, 0.5)"
                  pointerEvents="none"
                />
              )}
            </Box>

            <Text variant="caption" color="stone" mt={4}>
              Drag image to reposition.
            </Text>
            <Text
              data-testid="editPhotoModal-scaleText"
              variant="caption"
              mt={6}
            >
              Scale • {Math.round(zoom * 100)}%
            </Text>
            <Slider
              mt={4}
              aria-label="slider-ex-2"
              colorScheme="pink"
              min={1}
              max={5}
              step={0.01}
              value={zoom}
              onChange={setZoom}
            >
              <SliderTrack>
                <SliderFilledTrack backgroundColor="onyx" />
              </SliderTrack>
              <SliderThumb boxSize="28px" />
            </Slider>
          </Flex>
        </ModalBody>

        <ModalFooter>
          <Button
            variant="secondary"
            width="100%"
            height="44px"
            onClick={onClickSave}
            isLoading={loading}
            loadingText="Uploading photo"
            data-testid="editPhotoModal-saveButton"
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default EditPhotoModal
